/**
 * CancelConsultDialog — accessible confirm dialog (role=alertdialog) for
 * cancelling a booked consultation. NO window.confirm. Makes clear the client
 * is notified, calls the team-auth `cancelConsultation` seam, and shows honest
 * loading/error — the dialog only closes on a real success.
 */
import { useState } from 'react';
import AdminDialogShell from './AdminDialogShell';
import type { AdminConsultation } from '../lib/consultAdminStore';
import type { ConsultAdminApi } from '../lib/consultAdminApi';

export default function CancelConsultDialog({
  consult,
  api,
  whenLabel,
  onClose,
  onCancelled,
  announce,
}: {
  consult: AdminConsultation;
  api: ConsultAdminApi;
  /** Human "Tue, 8 Jul · 11:00–11:30 (IST)" for the body. */
  whenLabel: string;
  onClose: () => void;
  onCancelled: () => void;
  announce: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function confirmCancel() {
    if (busy) return;
    setBusy(true);
    setError('');
    announce('Cancelling the call…');
    const res = await api.cancel(consult.id);
    if (res.kind === 'ok') {
      announce('The call was cancelled and the client notified.');
      onCancelled();
    } else {
      setBusy(false);
      setError(res.message);
      announce(res.message);
    }
  }

  return (
    <AdminDialogShell
      role="alertdialog"
      labelledById="cad-cancel-title"
      describedById="cad-cancel-body"
      busy={busy}
      onClose={onClose}
    >
      <h2 className="cad-title" id="cad-cancel-title">
        Cancel this consultation?
      </h2>
      <p className="cad-sub" id="cad-cancel-body">
        This cancels the meeting with <b>{consult.clientName}</b> ({whenLabel}) and
        notifies the client automatically by email &amp; WhatsApp. The calendar
        event and Meet link are removed. This can&apos;t be undone — they&apos;d need
        to book again.
      </p>

      {error && (
        <p className="cad-err" role="alert">
          {error}
        </p>
      )}

      <div className="cad-actions">
        <button type="button" className="ca-btn ca-btn-line" onClick={onClose} disabled={busy}>
          Keep the booking
        </button>
        <button
          type="button"
          className="ca-btn ca-btn-danger"
          onClick={() => void confirmCancel()}
          disabled={busy}
        >
          {busy ? (
            <>
              <span className="cad-spinner" aria-hidden="true" /> Cancelling…
            </>
          ) : (
            'Cancel & notify client'
          )}
        </button>
      </div>
    </AdminDialogShell>
  );
}
